import { FC, useContext, useState, ChangeEvent } from 'react';
import Modal from './FiligraneBaseModal';
import { UserContext } from 'features/account';
import { shareFile } from 'features/invitation';
import { ProcessedResult } from './filigrane.types';
import { ReactComponent as IconSend } from 'assets/img/icons/icon_Send.svg';
import { ReactComponent as IconFile } from 'assets/img/icons/icon_File2.svg';
import './SendFileModal.scss';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Fichiers filigranés sélectionnés à envoyer */
  results: ProcessedResult[];
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SendFileModal: FC<Props> = ({ isOpen, onClose, results }) => {
  const { user } = useContext(UserContext);
  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sentCount, setSentCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const emailValid = EMAIL_REGEX.test(email.trim());

  const handleClose = () => {
    if (isSending) return;
    setEmail('');
    setErrorMessage(null);
    setSuccessMessage(null);
    setSentCount(0);
    onClose();
  };

  const handleSend = async () => {
    if (!emailValid || isSending || results.length === 0) return;
    setIsSending(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    setSentCount(0);

    const failures: string[] = [];
    let serverMessage: string | null = null;
    let sent = 0;

    for (const r of results) {
      try {
        const file = new File([r.blob], `filigrane_${r.name}`, { type: r.blob.type });
        await shareFile(email.trim(), file, user?.type);
        sent += 1;
        setSentCount(sent);
      } catch (err: any) {
        console.error(`Erreur d'envoi de ${r.name}:`, err);
        failures.push(r.name);
        if (!serverMessage) serverMessage = err?.response?.data?.message ?? null;
      }
    }

    setIsSending(false);
    if (failures.length === 0) {
      setSuccessMessage(
        results.length > 1
          ? 'Les documents ont bien été envoyés'
          : 'Le document a bien été envoyé'
      );
    } else {
      setErrorMessage(
        serverMessage ?? `Échec de l'envoi pour : ${failures.join(', ')}`
      );
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Envoyer par email" size="sm">
      <div className="send-file-modal">

        <p className="send-file-modal__intro">
          Renseignez l'email de la personne ou de l'organisme à qui envoyer
          {results.length > 1 ? ' ces documents filigranés.' : ' ce document filigrané.'}
        </p>

        <ul className="send-file-modal__files">
          {results.map(r => (
            <li key={r.id} className="send-file-modal__file">
              <IconFile fill="#1956A8" width={14} />
              <span>filigrane_{r.name}</span>
            </li>
          ))}
        </ul>

        <label className="send-file-modal__label" htmlFor="send-file-email">
          Email du destinataire
        </label>
        <input
          id="send-file-email"
          className="send-file-modal__input"
          type="email"
          placeholder="exemple@domaine.fr"
          value={email}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
          disabled={isSending || !!successMessage}
        />

        {errorMessage && (
          <div className="send-file-modal__alert send-file-modal__alert--error" role="alert">
            {errorMessage}
          </div>
        )}
        {successMessage && (
          <div className="send-file-modal__alert send-file-modal__alert--success" role="status">
            {successMessage}
          </div>
        )}

        <div className="send-file-modal__actions">
          {successMessage ? (
            <button className="send-file-modal__send-btn" onClick={handleClose} type="button">
              Fermer
            </button>
          ) : (
            <>
              <button
                className="send-file-modal__cancel-btn"
                onClick={handleClose}
                type="button"
                disabled={isSending}
              >
                Annuler
              </button>
              <button
                className="send-file-modal__send-btn"
                onClick={handleSend}
                type="button"
                disabled={!emailValid || isSending || results.length === 0}
              >
                {isSending ? (
                  <>
                    <span className="send-file-modal__spinner" aria-hidden="true" />
                    <span>
                      Envoi…{results.length > 1 ? ` (${sentCount}/${results.length})` : ''}
                    </span>
                  </>
                ) : (
                  <>
                    <IconSend fill="#fff" width={16} />
                    <span>Envoyer</span>
                  </>
                )}
              </button>
            </>
          )}
        </div>

      </div>
    </Modal>
  );
};

export default SendFileModal;
